import { formaters } from "vincent-utils";
import _ from "underts";
import formatThreadsForDisplay from "./formatThreadsForDisplay";
import { ThreadsId } from "cadendar-shared";

interface Patient {
  id: string;
  name: string;
  forename: string;
}

interface Message {
  patient_id: string | null;
  thread_id: ThreadsId;
  author_doctor_id: string | null;
  author_secretary_id: string | null;
  timestamp: Date;
  seen_by: string[];
}

interface User {
  id: string;
  messagename: string;
  tag: "doctor" | "secretary" | "app";
  organization_id: number;
}

function threadLastMessageDate(
  thread: {
    timestamp: Date;
  }[]
) {
  return thread.reduce((acc, obj) => {
    return obj.timestamp.getTime() > acc.getTime() ? obj.timestamp : acc;
  }, new Date(0));
}

function addAuthorTypeToMessage<M extends Message>(
  message: M,
  user: User,
  allUsers: User[]
) {
  if (!message.author_doctor_id && !message.author_secretary_id) {
    return {
      ...message,
      authorType: "caduser" as const,
    };
  }
  if (user.tag === "doctor") {
    if (message.author_doctor_id === user.id) {
      return {
        ...message,
        authorType: "myself" as const,
      };
    }
    const author = allUsers.find((u) => u.id === message.author_doctor_id);
    if (author?.organization_id === user.organization_id) {
      return {
        ...message,
        authorType: "team" as const,
      };
    }
    return {
      ...message,
      authorType: "otherTeam" as const,
    };
  }
  if (user.tag === "secretary") {
    if (message.author_secretary_id === user.id) {
      return {
        ...message,
        authorType: "myself" as const,
      };
    }
    const author = allUsers.find((u) => u.id === message.author_secretary_id);
    if (author?.organization_id === user.organization_id) {
      return {
        ...message,
        authorType: "team" as const,
      };
    }
    return {
      ...message,
      authorType: "otherTeam" as const,
    };
  }
  throw new Error("Case not handled in addAuthorTypeToMessage");
}

export function messagesToThreadsPipeline<
  M extends Message & { name: string | null; forename: string | null },
>(messages: M[], user: User, allUsers: User[]) {
  const messagesWithPatientsNames = messages.map((msg) => {
    return {
      ...msg,
      patient_name: `${msg.name?.toLocaleUpperCase() || ""} ${formaters.capitaliseEveryFirstLetter(
        msg.forename || ""
      )}`,
    };
  });
  const messagesWithAuthorType = messagesWithPatientsNames.map((msg) =>
    addAuthorTypeToMessage(msg, user, allUsers)
  );
  const grouped = _.groupByFunc(messagesWithAuthorType, (msg) => msg.thread_id);

  const threads = Object.values(grouped);
  const convertedThreads =
    threads && user
      ? threads.map((thread) =>
          formatThreadsForDisplay(thread, new Date(), allUsers, user.id)
        )
      : [];
  const sortedThreads = convertedThreads.sort((a, b) => {
    return threadLastMessageDate(a).getTime() >
      threadLastMessageDate(b).getTime()
      ? -1
      : 1;
  });
  return sortedThreads;
}
